import React, { FC, useState } from 'react';
import { Collapse, IconButton, Link, makeStyles, TableCell, TableRow, Theme, Tooltip, Typography } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import HistoryIcon from '@material-ui/icons/History';
import Skeleton from 'react-loading-skeleton';
import BodyCell from 'components/BodyCell';
import RecordLink from 'components/RecordLink';
import { format } from 'date-fns';

import { APP_LOG_ACTION_LABELS, APP_LOG_ENTITY_LABELS, APP_LOG_ENTITY_RECORD_TYPES, APP_LOG_SOURCE_LABELS } from 'constants/appLog';
import AppLogDetail from '../../AppLogDetail';

interface Props {
  isLoadingData: boolean;
  appLog: AppLogModel;
  index: number;
  columnCount: number;
  onFilterUser: (user: { id: number; name: string }) => void;
  onFilterEntity: (entity: { type: string; id: number; label: string }) => void;
  onFilterRequest: (requestId: string) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  tableRow: {
    height: 64,
    '& > td': {
      borderBottom: 'unset'
    }
  },
  detailCell: {
    padding: 0,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  secondaryText: {
    color: theme.palette.text.secondary
  },
  linkButton: {
    textAlign: 'left'
  },
  recordCell: {
    display: 'flex',
    alignItems: 'center'
  },
  historyButton: {
    marginLeft: theme.spacing(0.5),
    padding: theme.spacing(0.5)
  }
}));

const BodyRow: FC<Props> = props => {
  const classes = useStyles();
  const { isLoadingData, appLog, columnCount, onFilterUser, onFilterEntity, onFilterRequest } = props;
  const { id, user, userId, description, createdAt, source, action, entityType, entityId, entityLabel } = appLog;

  const [open, setOpen] = useState<boolean>(false);

  // logs written before the structured details were introduced only have a description
  const hasDetail = !!source;
  const recordType = entityType ? APP_LOG_ENTITY_RECORD_TYPES[entityType] : undefined;
  const recordText = entityLabel || (entityId ? `#${entityId}` : '');

  const renderText = (text: React.ReactNode, width = 120) => (
    <Typography variant='body2' component='div'>
      {isLoadingData ? <Skeleton width={width} /> : text || '-'}
    </Typography>
  );

  const renderUser = () => (
    <>
      {userId ? (
        <Link component='button' variant='body2' className={classes.linkButton} onClick={() => onFilterUser({ id: userId, name: user })}>
          {user}
        </Link>
      ) : (
        user
      )}
      {source && (
        <Typography variant='caption' className={classes.secondaryText} component='div'>
          {APP_LOG_SOURCE_LABELS[source] || source}
        </Typography>
      )}
    </>
  );

  // e.g. "Edit · Invoice" above the record name
  const renderRecord = () => {
    if (!action && !entityType) {
      return '-';
    }

    const actionAndModule = [action && (APP_LOG_ACTION_LABELS[action] || action), entityType && (APP_LOG_ENTITY_LABELS[entityType] || entityType)]
      .filter(Boolean)
      .join(' · ');

    return (
      <div className={classes.recordCell}>
        <div>
          <Typography variant='caption' className={classes.secondaryText} component='div'>
            {actionAndModule}
          </Typography>
          {recordType && entityId ? (
            <RecordLink recordType={recordType} recordId={entityId} recordLabel={entityLabel} className={classes.linkButton}>
              {recordText}
            </RecordLink>
          ) : (
            recordText
          )}
        </div>
        {entityType && entityId && (
          <Tooltip title='Show history of this record'>
            <IconButton
              size='small'
              className={classes.historyButton}
              onClick={() => onFilterEntity({ type: entityType, id: entityId, label: recordText })}
            >
              <HistoryIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
      </div>
    );
  };

  return (
    <>
      <TableRow className={classes.tableRow}>
        <BodyCell cellWidth='5%' isComponent={true}>
          {!isLoadingData && hasDetail && (
            <IconButton size='small' onClick={() => setOpen(!open)} aria-label={open ? 'Hide detail' : 'Show detail'}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </BodyCell>
        <BodyCell cellWidth='13%' isComponent={true}>
          {renderText(
            <>
              {format(new Date(createdAt), 'dd-MM-yyyy')}
              <div className={classes.secondaryText}>{format(new Date(createdAt), 'hh:mm:ss a')}</div>
            </>
          )}
        </BodyCell>
        <BodyCell cellWidth='15%' isComponent={true}>
          {renderText(renderUser())}
        </BodyCell>
        <BodyCell cellWidth='20%' isComponent={true}>
          {renderText(renderRecord())}
        </BodyCell>
        <BodyCell cellWidth='47%' isComponent={true}>
          {renderText(description, 200)}
        </BodyCell>
      </TableRow>
      <TableRow>
        <TableCell className={classes.detailCell} colSpan={columnCount}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <AppLogDetail appLogId={id} onFilterRequest={onFilterRequest} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default BodyRow;
